import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Plus, Trash2, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminEventTeams = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [registeredTeams, setRegisteredTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [schoolNameSet, setSchoolNameSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [formData, setFormData] = useState({
    team_id: '',
    category: '',
  });

  const { toast } = useToast();

  useEffect(() => {
    loadEvents();
    loadAllTeams();
    loadSchoolReferences();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await apiService.admin.getEvents();
      setEvents(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat event', variant: 'destructive' });
    }
  };

  const loadAllTeams = async () => {
    try {
      const response = await apiService.admin.getParticipants();
      setAllTeams(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat tim', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadSchoolReferences = async () => {
    try {
      const response = await apiService.admin.getSchoolReference();
      const rows = response.data || [];
      const names = rows.map((r) => (r.name || '').trim().toLowerCase()).filter(Boolean);
      setSchoolNameSet(new Set(names));
    } catch (error) {
      console.error('Failed to load school references:', error);
    }
  };

  const handleSelectEvent = async (eventId) => {
    setSelectedEventId(eventId);
    const event = events.find(e => e.id === eventId);
    setSelectedEvent(event);
    
    try {
      const response = await apiService.admin.getEventTeams(eventId);
      setRegisteredTeams(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat tim event', variant: 'destructive' });
      setRegisteredTeams([]);
    }
  };

  const handleRegisterTeam = async (e) => {
    e.preventDefault();
    
    if (!selectedEventId || !formData.team_id || !formData.category) {
      toast({ title: 'Error', description: 'Lengkapi semua field', variant: 'destructive' });
      return;
    }

    // Check if team already registered in this category
    const alreadyRegistered = registeredTeams.some(
      reg => reg.team_id === formData.team_id && reg.category === formData.category
    );
    if (alreadyRegistered) {
      toast({ title: 'Error', description: 'Tim sudah terdaftar di kategori ini', variant: 'destructive' });
      return;
    }

    try {
      await apiService.admin.registerTeamToEvent(selectedEventId, {
        team_id: formData.team_id,
        category: formData.category,
      });

      // Refresh registered teams
      const response = await apiService.admin.getEventTeams(selectedEventId);
      setRegisteredTeams(response.data || []);

      toast({ title: 'Success', description: 'Tim berhasil didaftarkan ke event' });
      setFormData({ team_id: '', category: '' });
      setShowRegistrationModal(false);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Gagal mendaftarkan tim', variant: 'destructive' });
    }
  };

  const handleRemoveTeam = async (registration) => {
    if (!window.confirm(`Hapus ${getTeamName(registration.team_id)} dari event ini?`)) {
      return;
    }

    try {
      await apiService.admin.unregisterTeamFromEvent(selectedEventId, registration.id);
      
      // Refresh registered teams
      const response = await apiService.admin.getEventTeams(selectedEventId);
      setRegisteredTeams(response.data || []);
      
      toast({ title: 'Success', description: 'Tim berhasil dihapus dari event' });
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal menghapus tim dari event', variant: 'destructive' });
    }
  };

  const handleUpdateStatus = async (registration, nextStatus) => {
    try {
      await apiService.admin.updateEventTeamStatus(selectedEventId, registration.id, nextStatus);
      const response = await apiService.admin.getEventTeams(selectedEventId);
      setRegisteredTeams(response.data || []);
      toast({ title: 'Success', description: `Status tim diubah menjadi ${nextStatus}` });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Gagal mengubah status tim',
        variant: 'destructive',
      });
    }
  };

  const getTeamName = (teamId) => {
    return allTeams.find(t => t.id === teamId)?.name || teamId;
  };

  const getEventName = (eventId) => {
    return events.find(e => e.id === eventId)?.name || eventId;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'U-10': 'bg-blue-100 text-blue-800',
      'U-12': 'bg-green-100 text-green-800',
      'U-14': 'bg-purple-100 text-purple-800',
      'U-17': 'bg-orange-100 text-orange-800',
      'U-21': 'bg-red-100 text-red-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const filteredTeams = allTeams.filter((team) => {
    if (!selectedEvent) return true;

    const explicitTypeRaw = (team.participant_type || '').trim().toLowerCase();
    const explicitType = explicitTypeRaw === 'sekolah'
      ? 'school'
      : explicitTypeRaw === 'klub'
        ? 'club'
        : explicitTypeRaw;

    const teamName = (team.name || '').trim().toLowerCase();
    const fallbackType = schoolNameSet.has(teamName) ? 'school' : 'club';
    const teamType = explicitType === 'school' || explicitType === 'club' ? explicitType : fallbackType;

    if (selectedEvent.event_target === 'school') return teamType === 'school';
    if (selectedEvent.event_target === 'club') return teamType === 'club';
    return true;
  });

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      registered: 'bg-green-100 text-green-800',
      active: 'bg-green-100 text-green-800',
      withdrawn: 'bg-orange-100 text-orange-800',
      disqualified: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">Memuat...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 p-4 sm:p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Pendaftaran Tim Event</h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">Kelola pendaftaran tim dan buat drawing bracket turnamen</p>
        </div>

        {/* Event Selection */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-lg font-semibold mb-4">Pilih Event</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {events.map(event => (
              <button
                key={event.id}
                onClick={() => handleSelectEvent(event.id)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  selectedEventId === event.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <h3 className="font-semibold text-gray-900">{event.name}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Jenis: {event.event_target === 'school' ? 'Sekolah' : 'Klub'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Format: {event.format === 'league_round_robin' ? 'Liga' : 'Turnamen'}
                </p>
                <p className="text-sm text-gray-600">
                  Kategori: {event.categories?.join(', ') || '-'}
                </p>
                <p className="text-xs text-gray-500 mt-2">Status: {event.status}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Team Registration */}
        {selectedEvent && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
              <h2 className="text-lg font-semibold">Tim Terdaftar - {selectedEvent.name}</h2>
              <button
                onClick={() => setShowRegistrationModal(true)}
                className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium"
              >
                <Plus size={20} />
                Tambah Tim
              </button>
            </div>

            {/* Registered Teams List */}
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="w-full text-sm sm:text-base">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 font-semibold text-gray-900">Nama Tim</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-900">Kategori</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-900">Status</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-900">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {registeredTeams.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center py-8 text-gray-500">
                        Belum ada tim yang terdaftar
                      </td>
                    </tr>
                  ) : (
                    registeredTeams.map(registration => (
                      <tr key={`${registration.team_id}-${registration.category}`} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">
                            {getTeamName(registration.team_id)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(registration.category)}`}>
                            {registration.category}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(registration.status)}`}>
                            {registration.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="inline-flex items-center gap-3">
                            {registration.status !== 'registered' && (
                              <button
                                onClick={() => handleUpdateStatus(registration, 'registered')}
                                className="text-green-700 hover:text-green-900 inline-flex items-center gap-1"
                              >
                                <span className="text-sm">Jadikan Terdaftar</span>
                              </button>
                            )}
                            <button
                              onClick={() => handleRemoveTeam(registration)}
                              className="text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                            >
                              <Trash2 size={18} />
                              <span className="text-sm">Hapus</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

          </div>
        )}

        {/* Registration Modal */}
        {showRegistrationModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Daftarkan Tim ke Event</h3>
                <button
                  onClick={() => setShowRegistrationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleRegisterTeam} className="space-y-4">
                {/* Team Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pilih Tim
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Menampilkan peserta {selectedEvent.event_target === 'school' ? 'sekolah' : 'klub'} sesuai jenis event.
                  </p>
                  <select
                    value={formData.team_id}
                    onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">-- Pilih Tim --</option>
                    {filteredTeams.map(team => {
                      const alreadyRegistered = formData.category
                        ? registeredTeams.some(reg => reg.team_id === team.id && reg.category === formData.category)
                        : false;
                      return (
                        <option key={team.id} value={team.id} disabled={alreadyRegistered}>
                          {team.name} {alreadyRegistered && '(Sudah daftar di kategori ini)'}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pilih Kategori
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">-- Pilih Kategori --</option>
                    {selectedEvent.categories?.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowRegistrationModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Daftarkan
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEventTeams;
